import React, { useEffect, useRef, useState } from "react";
import { Fov } from "../../../Model/ModuleState";
import { BaseControl, FeatureLabel, UnitLabel } from "../ImagerFeatureControls";
import "./FovControl.css";

export interface FovChanged {
  width?: number;
  height?: number;
}

type Props = {
  fov: Fov;
  maxFov: Fov;
  minFov: Fov;
  onValueChanged: (id: string, fov: FovChanged) => void;
  disabled: boolean;
  id: string;
};

export default function FovControl({
  fov,
  maxFov,
  minFov,
  onValueChanged,
  disabled,
  id,
}: Props) {
  const ratio = fov.width / fov.height;
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const inputWidthRef = useRef<HTMLInputElement>(null);
  const inputHeightRef = useRef<HTMLInputElement>(null);
  const [isError, setIsError] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(fov.width.toFixed(0));
  const [currentHeight, setCurrentHeight] = useState(fov.height.toFixed(0));

  useEffect(() => {
    setCurrentWidth(fov.width.toFixed(0));
    setCurrentHeight(fov.height.toFixed(0));
  }, [fov]);

  const repeatIncreaseWidthValue = () => {
    let reachMax = false;
    if (inputWidthRef.current != null && inputHeightRef.current != null) {
      let newWidth = Number.parseInt(inputWidthRef.current.value) + 1;
      if (newWidth > maxFov.width) {
        newWidth = maxFov.width;
        reachMax = true;
      }
      const newHeight = newWidth / ratio;
      inputWidthRef.current.value = newWidth.toFixed(0);
      inputHeightRef.current.value = newHeight.toFixed(0);
    }
    if (!reachMax) {
      timerRef.current = setTimeout(repeatIncreaseWidthValue, 100);
    }
  };

  const repeatIncreaseHeightValue = () => {
    let reachMax = false;
    if (inputWidthRef.current != null && inputHeightRef.current != null) {
      let newHeight = Number.parseInt(inputHeightRef.current.value) + 1;
      if (newHeight > maxFov.height) {
        newHeight = maxFov.height;
        reachMax = true;
      }
      const newWidth = ratio * newHeight;
      inputWidthRef.current.value = newWidth.toFixed(0);
      inputHeightRef.current.value = newHeight.toFixed(0);
    }
    if (!reachMax) {
      timerRef.current = setTimeout(repeatIncreaseWidthValue, 100);
    }
  };

  const repeatDecreaseWidthValue = () => {
    let reachMin = false;
    if (inputWidthRef.current != null && inputHeightRef.current != null) {
      let newWidth = Number.parseInt(inputWidthRef.current.value) - 1;
      if (newWidth < minFov.width) {
        newWidth = minFov.width;
        reachMin = true;
      }
      const newHeight = newWidth / ratio;
      inputWidthRef.current.value = newWidth.toFixed(0);
      inputHeightRef.current.value = newHeight.toFixed(0);
    }
    if (!reachMin) timerRef.current = setTimeout(repeatDecreaseWidthValue, 100);
  };

  const repeatDecreaseHeightValue = () => {
    let reachMin = false;
    if (inputWidthRef.current != null && inputHeightRef.current != null) {
      let newHeight = Number.parseInt(inputHeightRef.current.value) - 1;
      if (newHeight < minFov.height) {
        newHeight = minFov.height;
        reachMin = true;
      }
      const newWidth = ratio * newHeight;
      inputWidthRef.current.value = newWidth.toFixed(0);
      inputHeightRef.current.value = newHeight.toFixed(0);
    }
    if (!reachMin)
      timerRef.current = setTimeout(repeatDecreaseHeightValue, 100);
  };

  const onWidthMouseDown = (isUp: boolean) => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
    }
    if (isUp) {
      if (fov.width < maxFov.width) {
        repeatIncreaseWidthValue();
      }
    } else {
      repeatDecreaseWidthValue();
    }
  };

  const onHeightMouseDown = (isUp: boolean) => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
    }
    if (isUp) {
      repeatIncreaseHeightValue();
    } else {
      repeatDecreaseHeightValue();
    }
  };

  const onMouseUp = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
    }

    if (inputWidthRef.current != null && inputHeightRef.current != null) {
      const newWidth = Number.parseInt(inputWidthRef.current.value);
      const newHeight = Number.parseInt(inputHeightRef.current.value);
      onValueChanged(id, {
        width: newWidth,
        height: newHeight,
      });
    }
  };

  const onValueUpdated = (value: string, isWidth: boolean) => {
    let numberValue = parseFloat(value);
    if (isNaN(numberValue)) {
      setIsError(true);
      return;
    }

    let clippedValue = numberValue;
    clippedValue = Math.min(
      clippedValue,
      isWidth ? maxFov.width : maxFov.height
    );
    clippedValue = Math.max(clippedValue, 1);
    if (isWidth) {
      onValueChanged(id, {
        width: clippedValue,
      });
    } else {
      onValueChanged(id, {
        height: clippedValue,
      });
    }
    setIsError(false);
  };

  const onKeyPressWidth = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      inputWidthRef.current?.blur();
    }
  };

  const onKeyPressHeight = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      inputHeightRef.current?.blur();
    }
  };

  return (
    <BaseControl id={id} key={id} disabled={disabled}>
      <div style={{ lineHeight: "28px" }}>
        <FeatureLabel disabled={disabled}>FOV:</FeatureLabel>
      </div>
      <div className="fov-control-container">
        <div
          className="fov-control"
          style={{
            gridArea: "fov-width",
          }}
        >
          <FovLabel
            style={{
              gridArea: "fov-control-label",
              paddingRight: 5,
            }}
            disabled={disabled}
          >
            Width
          </FovLabel>
          <div
            className="fov-spinner-text-box"
            style={{
              border: disabled ? "1px solid #CCCCCC" : "1px solid #4D4D4D",
              gridArea: "fov-control-input",
            }}
          >
            <input
              ref={inputWidthRef}
              onKeyPress={onKeyPressWidth}
              onBlur={(e) => onValueUpdated(e.target.value, true)}
              onChange={(e) => {
                setCurrentWidth(e.target.value);
              }}
              style={{
                gridArea: "input",
                backgroundColor: isError ? "rgb(233, 120, 126)" : disabled ? "-internal-light-dark(rgba(239, 239, 239, 0.3), rgba(59, 59, 59, 0.3))":"transparent",
              }}
              type="number"
              min={1}
              max={maxFov.width}
              value={currentWidth}
              disabled={disabled}
            />
            <button
              tabIndex={1}
              onMouseDown={() => onWidthMouseDown(true)}
              onMouseUp={onMouseUp}
              style={{
                gridArea: "arrow-up",
              }}
              disabled={disabled}
            >
              <svg
                width="11"
                height="7"
                viewBox="0 0 11 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.9363 6.45459L5.93628 0.45459L0.936279 6.45459H4.06128H4.68628L7.18628 6.45459H7.81128H10.9363Z"
                  fill="#4D4D4D"
                />
              </svg>
            </button>
            <button
              tabIndex={1}
              onMouseDown={() => onWidthMouseDown(false)}
              onMouseUp={onMouseUp}
              style={{
                gridArea: "arrow-down",
              }}
              disabled={disabled}
            >
              <svg
                width="11"
                height="7"
                viewBox="0 0 11 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.9363 0.54541L5.93628 6.54541L0.936279 0.54541H4.06128H4.68628L7.18628 0.54541H7.81128H10.9363Z"
                  fill="#4D4D4D"
                />
              </svg>
            </button>
          </div>
          <UnitLabel
            style={{
              gridArea: "fov-control-unit",
            }}
            disabled={disabled}
          >
            &#181;m
          </UnitLabel>
        </div>

        <div
          className="fov-control"
          style={{
            gridArea: "fov-height",
          }}
        >
          <FovLabel
            style={{
              gridArea: "fov-control-label",
              paddingRight: 5,
            }}
            disabled={disabled}
          >
            Height
          </FovLabel>
          <div
            className="fov-spinner-text-box"
            style={{
              border: disabled ? "1px solid #CCCCCC" : "1px solid #4D4D4D",
              gridArea: "fov-control-input",
            }}
          >
            <input
              ref={inputHeightRef}
              onKeyPress={onKeyPressHeight}
              onBlur={(e) => onValueUpdated(e.target.value, false)}
              onChange={(e) => {
                setCurrentHeight(e.target.value);
              }}
              style={{
                gridArea: "input",
                backgroundColor: isError ? "rgb(233, 120, 126)" :  disabled ? "-internal-light-dark(rgba(239, 239, 239, 0.3), rgba(59, 59, 59, 0.3))" :"transparent",
              }}
              type="number"
              min={1}
              max={maxFov.height}
              value={currentHeight}
              disabled={disabled}
            />
            <button
              tabIndex={1}
              onMouseDown={() => onHeightMouseDown(true)}
              onMouseUp={onMouseUp}
              style={{
                gridArea: "arrow-up",
              }}
              disabled={disabled}
            >
              <svg
                width="11"
                height="7"
                viewBox="0 0 11 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.9363 6.45459L5.93628 0.45459L0.936279 6.45459H4.06128H4.68628L7.18628 6.45459H7.81128H10.9363Z"
                  fill="#4D4D4D"
                />
              </svg>
            </button>
            <button
              tabIndex={1}
              onMouseDown={() => onHeightMouseDown(false)}
              onMouseUp={onMouseUp}
              style={{
                gridArea: "arrow-down",
              }}
              disabled={disabled}
            >
              <svg
                width="11"
                height="7"
                viewBox="0 0 11 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.9363 0.54541L5.93628 6.54541L0.936279 0.54541H4.06128H4.68628L7.18628 0.54541H7.81128H10.9363Z"
                  fill="#4D4D4D"
                />
              </svg>
            </button>
          </div>
          <UnitLabel
            style={{
              gridArea: "fov-control-unit",
            }}
            disabled={disabled}
          >
            &#181;m
          </UnitLabel>
        </div>
      </div>
    </BaseControl>
  );
}

type FovLabelProps = {
  children: React.ReactNode;
  disabled: boolean;
  style?: React.CSSProperties;
};

function FovLabel({ children, disabled, style }: FovLabelProps) {
  return (
    <div
      style={{
        textAlign: "right",
        minWidth: 72,
        color: disabled ? "#808080" : "#000000",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
